import React from 'react'
import classNames from 'classnames'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import {
  SaveableSellableFragment,
  SaveSellableComponent,
  UnsaveSellableComponent,
  SellableCardFragment
} from '#src/generated/graphql'
import { Button, CircleLoader } from '@deal/components'
import styles from './styles.css'
import HeartOutline from './heart-outline.svg'
import HeartFilled from './heart-filled.svg'
import { getSellableEcommerceProperties } from '#src/services/tracking'

interface SaveSellableProps {
  sellable: SaveableSellableFragment
  addLabel?: string
  removeLabel?: string
  containerClassName?: string
  iconClassName?: string
}

interface MutateSavedSellableProps {
  sellableId: string
  addLabel?: string
  removeLabel?: string
  iconClassName?: string
  onComplete: (sellable: SellableCardFragment) => void
}

const SaveSellableButton: React.SFC<MutateSavedSellableProps> = ({
  sellableId,
  onComplete,
  addLabel,
  iconClassName
}) => {
  return (
    <SaveSellableComponent
      variables={{
        input: {
          sellableId
        }
      }}
      onCompleted={response => {
        if (response && response.saveSavedSellable) {
          onComplete(response.saveSavedSellable.savedSellable.sellable)
        }
      }}
      refetchQueries={['UserSavedSellables', 'MyFavorites']}
    >
      {(saveSellable, { loading }) => (
        <Button
          variant="link"
          className={styles.button}
          type="button"
          disabled={loading}
          onClick={e => {
            e.stopPropagation()
            e.preventDefault()

            saveSellable()
          }}
        >
          {loading ? (
            <CircleLoader />
          ) : (
            <HeartOutline className={classNames(styles.icon, iconClassName)} />
          )}

          {addLabel && <span className={styles.label}>{addLabel}</span>}
        </Button>
      )}
    </SaveSellableComponent>
  )
}

const UnsaveSellableButton: React.SFC<MutateSavedSellableProps> = ({
  sellableId,
  onComplete,
  removeLabel,
  iconClassName
}) => {
  return (
    <UnsaveSellableComponent
      variables={{
        input: {
          sellableId
        }
      }}
      onCompleted={response => {
        if (response && response.unsaveSavedSellable) {
          onComplete(response.unsaveSavedSellable.savedSellable.sellable)
        }
      }}
      refetchQueries={['UserSavedSellables', 'MyFavorites']}
    >
      {(unsaveSellable, { loading }) => (
        <Button
          variant="link"
          type="button"
          disabled={loading}
          className={styles.button}
          onClick={e => {
            e.stopPropagation()
            e.preventDefault()

            unsaveSellable()
          }}
        >
          {loading ? (
            <CircleLoader />
          ) : (
            <HeartFilled className={classNames(styles.icon, iconClassName)} />
          )}

          {removeLabel && <span className={styles.label}>{removeLabel}</span>}
        </Button>
      )}
    </UnsaveSellableComponent>
  )
}

const SaveSellable: React.SFC<SaveSellableProps & WithAnalyticsProps> = ({
  sellable,
  containerClassName,
  iconClassName,
  removeLabel,
  addLabel,
  analytics
}) => {
  const sellableToSave = sellable.parent || sellable

  return (
    <div className={classNames(styles.container, containerClassName)}>
      {sellableToSave.savedSellable && sellableToSave.savedSellable.saved ? (
        <UnsaveSellableButton
          sellableId={sellableToSave.id}
          removeLabel={removeLabel}
          iconClassName={iconClassName}
          onComplete={sellable => {
            if (analytics) {
              analytics.productRemovedFromWishlist(getSellableEcommerceProperties(sellable))
            }
          }}
        />
      ) : (
        <SaveSellableButton
          sellableId={sellableToSave.id}
          addLabel={addLabel}
          iconClassName={iconClassName}
          onComplete={sellable => {
            if (analytics) {
              analytics.productAddedToWishlist(getSellableEcommerceProperties(sellable))
            }
          }}
        />
      )}
    </div>
  )
}

export default withAnalytics(SaveSellable)
